import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Avatar,
  TablePagination,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import locationIcon from "../../../../assets/images/MapPin.svg";
import {
  SuppliersDirectoryApiResponse,
  SupplierDirectoryItem,
} from "../../../../types/suppliers";
import styles from "./suppliersDirectory.module.scss";

interface SuppliersDirectoryProps {
  directoryData: SuppliersDirectoryApiResponse | null;
  loading: boolean;
  error: string | null;
  onViewDetails: (supplier: SupplierDirectoryItem) => void;
  onPageChange: (limit: number, offset: number) => void;
}

const SuppliersDirectory: React.FC<SuppliersDirectoryProps> = ({
  directoryData,
  loading,
  error,
  onViewDetails,
  onPageChange,
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  const getInitials = useCallback((name: string): string => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }, []);

  const getDdStatusConfig = useCallback((status: string) => {
    const statusLower = status.toLowerCase();
    const statusMap: Record<string, { label: string; color: string; bgColor: string }> = {
      active: { label: status, color: "#008236", bgColor: "#F0FDF4" },
      inactive: { label: status, color: "#f44336", bgColor: "#ffebee" },
      onboarding: { label: status, color: "#2196f3", bgColor: "#e3f2fd" },
      evaluation: { label: status, color: "#ff9800", bgColor: "#fff3e0" },
      draft: { label: status, color: "#9e9e9e", bgColor: "#f5f5f5" },
      approved: { label: status, color: "#4caf50", bgColor: "#e8f5e9" },
      information_requested: { label: status, color: "#ff9800", bgColor: "#fff3e0" },
      pending: { label: status, color: "#9c27b0", bgColor: "#f3e5f5" },
    };

    for (const [key, value] of Object.entries(statusMap)) {
      if (statusLower.includes(key)) {
        return value;
      }
    }

    return { label: status, color: "#666666", bgColor: "#f5f5f5" };
  }, []);

  const formatCurrency = useCallback((value: number): string => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(2)}M`;
    }
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(2)}K`;
    }
    return `$${value.toFixed(2)}`;
  }, []);

  const filteredSuppliers = useMemo(() => {
    if (!directoryData?.suppliers) {
      return [];
    }

    if (!searchQuery.trim()) {
      return directoryData.suppliers;
    }

    const query = searchQuery.toLowerCase();
    return directoryData.suppliers.filter(
      (supplier) =>
        supplier.supplierName.toLowerCase().includes(query) ||
        supplier.country.toLowerCase().includes(query) ||
        supplier.category.toLowerCase().includes(query) ||
        supplier.status.toLowerCase().includes(query),
    );
  }, [directoryData?.suppliers, searchQuery]);

  const handleChangePage = useCallback(
    (_event: unknown, newPage: number) => {
      const limit = directoryData?.pagination.limit || 10;
      const newOffset = newPage * limit;
      onPageChange(limit, newOffset);
    },
    [directoryData?.pagination.limit, onPageChange]
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newRowsPerPage = parseInt(event.target.value, 10);
      onPageChange(newRowsPerPage, 0);
    },
    [onPageChange]
  );

  // Loading state
  if (loading) {
    return (
      <Box className={styles.container}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box className={styles.container}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <Typography variant="body1" color="error">
            {error}
          </Typography>
        </Box>
      </Box>
    );
  }

  // No data state
  if (!directoryData) {
    return (
      <Box className={styles.container}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <Typography variant="body1">No suppliers data available</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Typography variant="h5" className={styles.title}>
          All Suppliers Directory
        </Typography>
        <TextField
          placeholder="Search suppliers..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={styles.searchField}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon className={styles.searchIcon} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer className={styles.tableContainer}>
        <Table className={styles.table}>
          <TableHead>
            <TableRow className={styles.tableHeaderRow}>
              <TableCell className={styles.tableHeaderCell}>
                SUPPLIER NAME
              </TableCell>
              <TableCell className={styles.tableHeaderCell}>COUNTRY</TableCell>
              <TableCell className={styles.tableHeaderCell}>STATUS</TableCell>
              <TableCell className={styles.tableHeaderCell}>CATEGORY</TableCell>
              <TableCell className={styles.tableHeaderCell} align="center">
                CONTRACTS
              </TableCell>
              <TableCell className={styles.tableHeaderCell} align="center">
                CERTIFICATES
              </TableCell>
              <TableCell className={styles.tableHeaderCell} align="right">
                TOTAL SPEND
              </TableCell>
              <TableCell className={styles.tableHeaderCell} align="center">
                ACTIONS
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSuppliers.map((supplier) => {
              const statusConfig = getDdStatusConfig(supplier.status);
              return (
                <TableRow key={supplier.supplierId} className={styles.tableRow}>
                  <TableCell className={styles.tableCell}>
                    <Box className={styles.supplierInfo}>
                      <Avatar className={styles.avatar}>
                        {getInitials(supplier.supplierName)}
                      </Avatar>
                      <Box className={styles.supplierDetails}>
                        <Typography className={styles.supplierName}>
                          {supplier.supplierName}
                        </Typography>
                        <Typography className={styles.supplierCode}>
                          SUP-{supplier.supplierId}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell className={styles.tableCell}>
                    <Box className={styles.countryCell}>
                      <img
                          src={locationIcon}
                          alt="location"
                          className={styles.locationIcon}
                        />
                      <Typography className={styles.countryText}>
                        {supplier.country || "N/A"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell className={styles.tableCell}>
                    <Chip
                      label={statusConfig.label}
                      className={styles.statusChip}
                      style={{
                        backgroundColor: statusConfig.bgColor,
                        color: statusConfig.color,
                      }}
                      size="small"
                    />
                  </TableCell>
                  <TableCell className={styles.tableCell}>
                    <Typography className={styles.categoryText}>
                      {supplier.category}
                    </Typography>
                  </TableCell>
                  <TableCell className={styles.tableCell} align="center">
                    <Typography className={styles.contractsText}>
                      {supplier.contractsCount}
                    </Typography>
                  </TableCell>
                  <TableCell className={styles.tableCell} align="center">
                    <Typography className={styles.contractsText}>
                      {supplier.certificatesCount.length}
                    </Typography>
                  </TableCell>
                  <TableCell className={styles.tableCell} align="right">
                    <Typography className={styles.spendText}>
                      {formatCurrency(supplier.totalSpend)}
                    </Typography>
                  </TableCell>
                  <TableCell className={styles.tableCell} align="center">
                    <Button
                      variant="outlined"
                      size="small"
                      className={styles.viewButton}
                      onClick={() => onViewDetails(supplier)}
                    >
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredSuppliers.length === 0 && !loading && (
        <Box className={styles.emptyState}>
          <Typography variant="body1" className={styles.emptyText}>
            No suppliers found matching your search.
          </Typography>
        </Box>
      )}

      <TablePagination
        component="div"
        count={directoryData.pagination.total}
        page={Math.floor(directoryData.pagination.offset / directoryData.pagination.limit)}
        onPageChange={handleChangePage}
        rowsPerPage={directoryData.pagination.limit}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        className={styles.pagination}
      />
    </Box>
  );
};

export default SuppliersDirectory;
